import { Anchor } from "client/Components";
import { WorkspaceLogo } from "./WorkspaceLogo";
import {
	ReactElement,
	RefObject,
	useCallback,
	useMemo,
	useRef
} from "react";
import { createRoot } from "react-dom/client";
import {
	ensureNotNil,
	setTrackingId
} from "client/Misc/utils";
import { useBrowserTitleStore } from "client_legacy/ndaw/browserTitle";
import styles from "./TitleBar.module.less";
import { MainMenu } from "./Menus/MainMenu/MainMenu";
import { GlobalSearch } from "./GlobalSearch/GlobalSearch";
import {
	MediaQueryKind,
	useResizeObserver,
	useWorkspaceMediaQuery
} from "client/Hooks";
import classNames from "classnames";
import {
	ClientBrandingConfiguration,
	WorkspaceHeaderStyle,
	userConfiguration
} from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { sanitizeUriForReact } from "client_legacy/ndaw/urlHelper";
import { BackButton } from "./BackButton";
import { useTitleBarNavigationStore } from "./titleBarNavigationStore";
import { UserProfileMenu } from "./UserProfileMenu/UserProfileMenu";
import { useUserProfileStore } from "client/Features/Settings/UserProfile/userProfileStore";
import { useMobileAppDialog } from "./Menus/ProfileMenu/MobileAppDialog";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import { ResponsiveTitle } from "./ResponsiveTitle";
import { PlusMenuButton } from "./PlusMenuButton";
import { useFeedbackDialog } from "client/Features/Feedback/FeedbackDialog";
import { getEnvironmentUrl } from "client_legacy/ndaw/navigator";

export const TitleBar = (): ReactElement => {
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);

	const title: string = useBrowserTitleStore(x => x.title);
	const rightPartContentRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const { width: rightPartContentWidth } = useResizeObserver(rightPartContentRef);

	const isBackButtonAvailable: boolean = useTitleBarNavigationStore(x => x.isBackButtonAvailable);
	const currentUserIdentity: UserIdentityModel | null = useUserProfileStore(x => x.currentUserIdentity);

	const [mobileDialog, openMobileDialog] = useMobileAppDialog();
	const [feedbackDialog, openFeedbackDialog] = useFeedbackDialog();

	const brandingConfiguration = useMemo(
		(): ClientBrandingConfiguration => {
			return userConfiguration().branding;
		},
		[]
	);

	const homeUrl = useMemo(
		(): string => {
			return sanitizeUriForReact(brandingConfiguration.customHomeButtonUrl)
				?? getEnvironmentUrl();
		},
		[brandingConfiguration.customHomeButtonUrl]
	);

	const openFeedbackDialogHandler = useCallback(
		(): void => {
			openFeedbackDialog();
		},
		[openFeedbackDialog]
	);

	const headerStyle: WorkspaceHeaderStyle = brandingConfiguration.workspaceHeaderStyle;

	return (
		<header
			{...setTrackingId("title-bar")}
			className={classNames(
				styles.root,
				{
					[styles.root_colored]: headerStyle === WorkspaceHeaderStyle.ColoredBackground
				}
			)}
		>
			<div
				className={styles.leftPart}
				style={{flexBasis: rightPartContentWidth}}
			>
				<div
					className={classNames(
						styles.backButtonWrapper,
						{[styles.backButtonWrapper_available]: isBackButtonAvailable}
					)}
				>
					<BackButton headerStyle={headerStyle} />
				</div>
				<Anchor
					className={styles.homeLink}
					href={homeUrl}
					trackingId="home-logo"
				>
					<WorkspaceLogo workspaceHeaderStyle={headerStyle}/>
				</Anchor>
				<div className={styles.logoDivider} />
				<h1
					{...setTrackingId("title")}
					className={styles.title}
					title={title}
				>
					{title}
				</h1>
			</div>
			<div className={styles.middlePart}>
				{isResponsive
					? (
						<ResponsiveTitle />
					)
					: (
						<div className={classNames(
							styles.searchWrapper,
							styles.searchWrapper_fullWidth
						)}>
							<GlobalSearch headerStyle={headerStyle} />
						</div>
					)
				}
			</div>
			<div
				{...setTrackingId("title-bar-right")}
				className={styles.rightPart}
				style={{flexBasis: rightPartContentWidth}}
			>
				<div
					className={styles.rightPartContent}
					ref={rightPartContentRef}
				>
					<div className={styles.menuWrapper}>
						<MainMenu headerStyle={headerStyle} />
					</div>
					<div className={styles.plusMenuButtonWrapper}>
						<PlusMenuButton headerStyle={headerStyle} />
					</div>
					<div className={styles.profileMenuWrapper}>
						{currentUserIdentity && (
							<UserProfileMenu
								currentUserIdentity={currentUserIdentity}
								onOpenMobileDialog={openMobileDialog}
								onOpenFeedbackDialog={openFeedbackDialogHandler}
							/>
						)}
					</div>
					{mobileDialog}
					{feedbackDialog}
				</div>
			</div>
		</header>
	);
};
TitleBar.displayName = "TitleBar";

export function init(): void {
	createRoot(
		ensureNotNil(
			document.getElementById("title-bar-host")
		)
	)
		.render(
			<TitleBar />
		);
}